import { useEffect, useRef, useState } from "react";
import { QrCode, Camera, Store, Wallet, CheckCircle2, ArrowRight, RefreshCw, X, SwitchCamera } from "lucide-react";
import jsQR from "jsqr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { formatRupiah } from "@/lib/dummyData";
import { O2OAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Alur transaksi Offline O2O (PRD 3.4.A):
// 1) scan  -> anggota memindai QR statis gerai (WebRTC kamera) atau input kode manual
// 2) bayar -> input nominal belanja, dibayar penuh dari saldo dompet
// 3) struk -> struk digital transaksi
export default function ScanQR() {
  const { user } = useAuth();
  const [phase, setPhase] = useState("scan");     // scan | pay | receipt
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [merchant, setMerchant] = useState(null);
  const [amount, setAmount] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [paying, setPaying] = useState(false);

  // Kamera aktif: "environment" = belakang (default), "user" = depan.
  const [facing, setFacing] = useState("environment");
  const facingRef = useRef("environment");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  // Hentikan kamera & loop pemindaian.
  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
  };

  useEffect(() => () => stopCamera(), []); // bersihkan saat komponen dilepas

  // Nyalakan kamera (sesuai arah pilihan) dan pindai QR tiap frame memakai jsQR.
  const startCamera = async (mode = facingRef.current) => {
    stopCamera(); // pastikan stream lama mati sebelum ganti kamera
    try {
      // "ideal" agar tetap jalan di perangkat yang hanya punya satu kamera (mis. laptop).
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode } },
        audio: false,
      });
      streamRef.current = stream;
      facingRef.current = mode;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setScanning(true);
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error("Izin kamera ditolak. Izinkan akses kamera di browser, atau pakai input kode manual.");
      } else if (err?.name === "NotFoundError") {
        toast.error("Kamera tidak ditemukan di perangkat ini. Gunakan input kode manual.");
      } else {
        toast.error("Tidak bisa mengakses kamera. Gunakan input kode manual.");
      }
    }
  };

  // Ganti kamera depan <-> belakang tanpa keluar dari mode scan.
  const switchCamera = async () => {
    const next = facingRef.current === "environment" ? "user" : "environment";
    setFacing(next);
    await startCamera(next);
  };

  // Loop: gambar frame video ke canvas, decode QR, jika ketemu -> resolve gerai.
  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      // Kamera depan tampil mirror — balik horizontal agar QR tetap terbaca jsQR.
      if (facingRef.current === "user") {
        ctx.save();
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      } else {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const qr = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "attemptBoth" });
      if (qr && qr.data) {
        stopCamera();
        resolveMerchant(qr.data);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  // Terjemahkan kode QR menjadi data gerai lewat backend.
  const resolveMerchant = async (code) => {
    try {
      const m = await O2OAPI.resolve(code);
      setMerchant(m);
      setPhase("pay");
      toast.success(`Gerai: ${m.shop_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || "QR tidak dikenali");
    }
  };

  // Bayar nominal belanja dari saldo dompet.
  const pay = async () => {
    const nominal = Number(amount);
    if (!nominal || nominal <= 0) return toast.error("Masukkan nominal belanja");
    setPaying(true);
    try {
      const r = await O2OAPI.pay({ shop_id: merchant.id, amount: nominal });
      setReceipt(r);
      setPhase("receipt");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Pembayaran gagal");
    } finally {
      setPaying(false);
    }
  };

  const reset = () => {
    setMerchant(null); setAmount(""); setReceipt(null); setManualCode(""); setPhase("scan");
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Scan QR Gerai</h1>
        <p className="text-ink/75 mt-2 text-sm">Bayar belanja di gerai fisik RT/RW pakai saldo dompet — harga anggota otomatis.</p>
      </div>

      {/* ====== FASE 1: SCAN ====== */}
      {phase === "scan" && (
        <Card className="rounded-3xl border-stone-soft p-6 space-y-5">
          <div className="aspect-square rounded-2xl bg-ink/90 relative overflow-hidden grid place-items-center">
            {/* Video kamera (WebRTC). Canvas tersembunyi untuk decoding. */}
            {/* Kamera depan ditampilkan mirror agar natural; decoding tetap pakai frame yang dibalik. */}
            <video ref={videoRef} className={`w-full h-full object-cover ${scanning ? "" : "hidden"} ${facing === "user" ? "scale-x-[-1]" : ""}`} playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            {!scanning && (
              <div className="text-center text-cream/70">
                <QrCode size={54} className="mx-auto" />
                <p className="text-sm mt-3">Arahkan kamera ke QR gerai</p>
              </div>
            )}
            {scanning && <div className="absolute inset-8 border-2 border-amber-warm rounded-2xl pointer-events-none animate-pulse" />}
          </div>

          {!scanning ? (
            <Button onClick={() => startCamera()} className="w-full rounded-full bg-forest hover:bg-forest-dark text-white h-12" data-testid="scan-start">
              <Camera size={16} /> Nyalakan Kamera
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={switchCamera} variant="outline" className="rounded-full h-12 flex-1" data-testid="scan-switch-camera">
                <SwitchCamera size={16} /> {facing === "environment" ? "Kamera Depan" : "Kamera Belakang"}
              </Button>
              <Button onClick={stopCamera} variant="outline" className="rounded-full h-12 flex-1"><X size={16} /> Hentikan</Button>
            </div>
          )}

          {/* Fallback: input kode manual (bila kamera tak tersedia, mis. di desktop) */}
          <div className="pt-2 border-t border-stone-soft">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Atau masukkan kode gerai</Label>
            <div className="flex gap-2 mt-2">
              <Input value={manualCode} onChange={(e) => setManualCode(e.target.value)} placeholder="cth. KOPGERAI:1" className="bg-cream border-stone-soft rounded-lg" data-testid="scan-manual-input" />
              <Button onClick={() => resolveMerchant(manualCode)} className="rounded-lg bg-forest text-white" data-testid="scan-manual-submit">Cek</Button>
            </div>
          </div>
        </Card>
      )}

      {/* ====== FASE 2: BAYAR ====== */}
      {phase === "pay" && merchant && (
        <Card className="rounded-3xl border-stone-soft p-6 space-y-5">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl bg-forest/10 text-forest grid place-items-center"><Store size={22} /></span>
            <div>
              <div className="font-display font-bold text-lg text-ink">{merchant.shop_name}</div>
              <div className="text-xs text-ink/55">{merchant.owner_name} · Gerai terverifikasi</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nominal Belanja</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="cth. 50000" className="bg-cream border-stone-soft rounded-lg h-12 text-lg" data-testid="scan-amount" autoFocus />
          </div>

          <div className="rounded-2xl bg-cream p-4 text-sm flex items-center gap-2">
            <Wallet size={16} className="text-forest" />
            Saldo dompet: <b>{formatRupiah(user?.wallet?.balance_buyer || 0)}</b>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full flex-1" onClick={reset}>Batal</Button>
            <Button onClick={pay} disabled={paying} className="rounded-full flex-1 bg-forest hover:bg-forest-dark text-white" data-testid="scan-pay">
              {paying ? "Memproses…" : "Bayar Sekarang"} <ArrowRight size={16} />
            </Button>
          </div>
        </Card>
      )}

      {/* ====== FASE 3: STRUK ====== */}
      {phase === "receipt" && receipt && (
        <Card className="rounded-3xl border-stone-soft p-6 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-sage/20 text-forest grid place-items-center mx-auto"><CheckCircle2 size={32} /></div>
          <div>
            <h2 className="font-display font-bold text-2xl text-ink">Pembayaran Berhasil</h2>
            <p className="text-ink/60 text-sm mt-1">{receipt.merchant}</p>
          </div>
          <div className="rounded-2xl bg-cream p-5 text-sm space-y-2 text-left">
            <Row l="No. Transaksi" v={receipt.order_number} mono />
            <Row l="Nominal" v={formatRupiah(receipt.nominal)} />
            <div className="border-t border-stone-soft pt-2 flex justify-between font-semibold text-ink">
              <span>Total Dibayar</span><span>{formatRupiah(receipt.total_paid)}</span>
            </div>
            <Row l="Sisa Saldo" v={formatRupiah(receipt.wallet_balance_after)} />
          </div>
          <Button onClick={reset} className="w-full rounded-full bg-forest hover:bg-forest-dark text-white h-12" data-testid="scan-again">
            <RefreshCw size={16} /> Transaksi Baru
          </Button>
        </Card>
      )}
    </div>
  );
}

const Row = ({ l, v, mono, accent }) => (
  <div className="flex justify-between">
    <span className="text-ink/60">{l}</span>
    <span className={`font-semibold ${accent ? "text-amber-700" : "text-ink"} ${mono ? "font-mono text-xs" : ""}`}>{v}</span>
  </div>
);
